import { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronRight, X, Check, Layers } from "lucide-react";
import { cn } from "../../lib/utils";
import { getTypologyConfig, getCategoryConfig } from "../../lib/categoryConfig";
import type { CategoryTypeGroup } from "../../hooks/useOperators";
import type { OperatorWithCoords } from "../../types/database";

export interface CategoryTypeSelectProps {
  categoryTypes: CategoryTypeGroup[];
  selectedCategories?: string[];
  selectedTypes?: string[];
  onCategoriesChange: (values: string[]) => void;
  onTypesChange: (values: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Labels for nicer display names */
  labelMap?: Record<string, string>;
  /** Color map for typology (currently unused, kept for API compatibility) */
  colorMap?: Record<string, string>;
  /** Filtered operators to calculate real counts */
  filteredOperators?: OperatorWithCoords[];
  /** All operators (unfiltered) to calculate total counts for each category/type */
  allOperators?: OperatorWithCoords[];
}

export function CategoryTypeSelect({
  categoryTypes,
  selectedCategories = [],
  selectedTypes = [],
  onCategoriesChange,
  onTypesChange,
  placeholder = "Categoria / Tipologia",
  disabled = false,
  className,
  labelMap = {},
  filteredOperators = [],
  allOperators = [],
}: CategoryTypeSelectProps) {

  // Calculate real counts for each category and type based on all operators (not filtered)
  // This ensures counts always show the total available, not just what's currently filtered
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    const typeCounts: Record<string, number> = {};

    // Use allOperators if available, otherwise fall back to filteredOperators
    const operatorsToCount = allOperators.length > 0 ? allOperators : filteredOperators;

    operatorsToCount.forEach((op) => {
      const category = op.macro_category;
      const typology = op.typology;

      if (category) {
        // Apply label mapping to match displayed category names
        const mappedCategory = labelMap[category] || category;
        // Count using the mapped name (what's displayed)
        counts[mappedCategory] = (counts[mappedCategory] || 0) + 1;
        // Also count original name in case it's used directly in the UI
        if (mappedCategory !== category) {
          counts[category] = (counts[category] || 0) + 1;
        }
      }

      if (typology) {
        // Apply label mapping to match displayed type names
        const mappedType = labelMap[typology] || typology;
        // Count using the mapped name (what's displayed)
        typeCounts[mappedType] = (typeCounts[mappedType] || 0) + 1;
        // Also count original name in case it's used directly in the UI
        if (mappedType !== typology) {
          typeCounts[typology] = (typeCounts[typology] || 0) + 1;
        }
      }
    });

    return { categoryCounts: counts, typeCounts };
  }, [allOperators, filteredOperators, labelMap]);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [userCollapsed, setUserCollapsed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearch("");
  }, []);

  // Auto-expand first selected category
  useEffect(() => {
    if (!expandedCategory && selectedCategories.length > 0 && !userCollapsed) {
      setExpandedCategory(selectedCategories[0]);
    }
  }, [selectedCategories, expandedCategory, userCollapsed]);

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        closeDropdown();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [closeDropdown]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeDropdown();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, closeDropdown]);

  // Filter categories and types based on search
  const filteredData = useMemo(() => {
    if (!search) return categoryTypes;

    const searchLower = search.toLowerCase();
    return categoryTypes
      .map((group) => {
        const categoryLabel = labelMap[group.category] || group.category;
        const categoryMatches = categoryLabel.toLowerCase().includes(searchLower);

        const matchingTypes = group.types.filter((type) => {
          const typeLabel = labelMap[type] || type;
          return typeLabel.toLowerCase().includes(searchLower);
        });

        // Include category if it matches or has matching types
        if (categoryMatches || matchingTypes.length > 0) {
          return {
            ...group,
            types: categoryMatches ? group.types : matchingTypes,
          };
        }
        return null;
      })
      .filter(Boolean) as CategoryTypeGroup[];
  }, [categoryTypes, search, labelMap]);

  // Auto-expand first match when searching
  useEffect(() => {
    if (search && filteredData.length > 0 && !userCollapsed) {
      setExpandedCategory(filteredData[0].category);
    }
  }, [search, filteredData, userCollapsed]);

  // Get display label - memoized to avoid useMemo dependency issues
  const getLabel = useMemo(
    () => (value: string) => labelMap[value] || value,
    [labelMap]
  );

  const toggleOpen = () => {
    if (disabled) return;
    setIsOpen((prev) => {
      const next = !prev;
      if (next) {
        // Focus input when opening
        setTimeout(() => inputRef.current?.focus(), 0);
      }
      if (!next) {
        setSearch("");
      }
      return next;
    });
  };

  // Display value for the button
  const displayValue = useMemo(() => {
    const typeCount = selectedTypes.length;
    const categoryCount = selectedCategories.length;

    if (typeCount === 0 && categoryCount === 0) return null;

    if (typeCount > 0) {
      const first = getLabel(selectedTypes[0]);
      const extra = typeCount - 1;
      return extra > 0 ? `${first} +${extra}` : first;
    }

    const firstCategory = getLabel(selectedCategories[0]);
    const extraCategories = categoryCount - 1;
    return extraCategories > 0 ? `${firstCategory} +${extraCategories}` : firstCategory;
  }, [selectedCategories, selectedTypes, getLabel]);

  const clearSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCategoriesChange([]);
    onTypesChange([]);
    setSearch("");
    setExpandedCategory(null);
    setUserCollapsed(false);
    closeDropdown();
  };

  const handleCategoryToggle = (category: string) => {
    setExpandedCategory((current) => {
      const next = current === category ? null : category;
      setUserCollapsed(next === null);
      return next;
    });
  };

  const toggleCategorySelection = (category: string) => {
    const isSelected = selectedCategories.includes(category);
    const next = isSelected
      ? selectedCategories.filter((c) => c !== category)
      : [...selectedCategories, category];

    onCategoriesChange(next);

    if (!isSelected) {
      const categoryGroup = categoryTypes.find((g) => g.category === category);
      if (categoryGroup) {
        const filteredTypes = selectedTypes.filter((t) => !categoryGroup.types.includes(t));
        if (filteredTypes.length !== selectedTypes.length) {
          onTypesChange(filteredTypes);
        }
      }
    }

    // Keep expanded for easier multi-selection
    setExpandedCategory((current) => {
      const nextExpanded = current === category && isSelected ? null : category;
      setUserCollapsed(nextExpanded === null);
      return nextExpanded;
    });
  };

  const toggleTypeSelection = (category: string, type: string) => {
    const isSelected = selectedTypes.includes(type);
    const nextTypes = isSelected
      ? selectedTypes.filter((t) => t !== type)
      : [...selectedTypes, type];
    onTypesChange(nextTypes);

    if (!isSelected && selectedCategories.includes(category)) {
      onCategoriesChange(selectedCategories.filter((c) => c !== category));
    }

    // Don't automatically add category when selecting a type
    // This allows users to filter by category AND type independently
    // The backend will apply both filters with AND logic, so operators must match
    // both the selected category AND the selected type
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    if (!isOpen) setIsOpen(true);
  };

  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  const isSomethingSelected = selectedCategories.length > 0 || selectedTypes.length > 0;

  // Icon shown when a single typology is selected, or when a single category is selected
  const primaryType = selectedTypes.length === 1 ? selectedTypes[0] : null;
  const primaryCategory = selectedTypes.length === 0 && selectedCategories.length === 1 ? selectedCategories[0] : null;

  // Find the category for the primary type if available
  const primaryTypeCategory = primaryType && categoryTypes.find(g => g.types.includes(primaryType))?.category;

  const selectedConfig = primaryType
    ? getTypologyConfig(primaryType, primaryTypeCategory)
    : primaryCategory
      ? getCategoryConfig(primaryCategory)
      : null;
  const selectedColor = selectedConfig?.color;
  const SelectedIcon = selectedConfig?.icon;

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <div
        className={cn(
          "flex items-center h-9 w-full rounded-lg border overlay-solid shadow-lg text-sm transition-all duration-200",
          "focus-within:border-[#284899]",
          isSomethingSelected ? "border-[#284899]" : "border-border",
          disabled && "opacity-50 cursor-not-allowed",
          "pl-2.5"
        )}
        onClick={() => !disabled && setIsOpen(true)}
      >
        {/* Icon for selected type or default layers icon */}
        {SelectedIcon && !isOpen ? (
          <SelectedIcon
            className="h-3.5 w-3.5 shrink-0"
            style={{ color: selectedColor }}
          />
        ) : (
          <Layers className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        )}

        {/* Color dot for selected type */}
        {selectedColor && !isOpen && (
          <div
            style={{
              backgroundColor: selectedColor,
              width: 6,
              height: 6,
              minWidth: 6,
              minHeight: 6,
              borderRadius: "50%",
              flexShrink: 0,
              marginLeft: 4,
              border: "1px solid rgba(255,255,255,0.3)",
            }}
          />
        )}

        <input
          ref={inputRef}
          type="text"
          value={isOpen ? search : displayValue || ""}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            "flex-1 bg-transparent text-foreground placeholder:text-muted-foreground focus:outline-none",
            "min-w-0 pr-1 ml-1.5",
            selectedColor && !isOpen && "ml-1"
          )}
        />
        <div className="flex items-center gap-0.5 pr-1.5">
          {isSomethingSelected && !disabled && (
            <button
              type="button"
              onClick={clearSelection}
              className="p-0.5 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-3 w-3" />
            </button>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toggleOpen();
            }}
            className="p-0.5 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={isOpen ? "Chiudi filtro" : "Apri filtro"}
          >
            <ChevronDown
              className={cn(
                "h-3.5 w-3.5 text-muted-foreground transition-transform duration-200",
                isOpen && "rotate-180"
              )}
            />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className={cn(
              "absolute z-50 mt-1 w-full min-w-[280px] max-h-[70vh] overflow-auto rounded-lg",
              "overlay-solid border border-border shadow-xl",
              "no-scrollbar"
            )}
          >
            {filteredData.length === 0 ? (
              <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                Nessun risultato
              </div>
            ) : (
              <div className="py-1">
                {filteredData.map((group) => {
                  const isExpanded = expandedCategory === group.category;
                  const isCategorySelected = selectedCategories.includes(group.category);

                  // Check if all types are variants of "Accomodation" - if so, don't show dropdown
                  const categoryLabel = labelMap[group.category] || group.category;
                  const isAccomodationCategory = categoryLabel.toLowerCase() === "accomodation" ||
                    group.category.toLowerCase() === "accomodation" ||
                    group.category.toLowerCase() === "hospitality";
                  const allTypesAreAccomodation = isAccomodationCategory &&
                    group.types.every(type => {
                      const typeLabel = labelMap[type] || type;
                      return typeLabel.toLowerCase().includes("accomodation") ||
                        typeLabel.toLowerCase().includes("hotel") ||
                        typeLabel.toLowerCase().includes("hospitality");
                    });
                  const shouldShowTypes = !allTypesAreAccomodation && group.types.length > 0;

                  return (
                    <div key={group.category}>
                      {/* Category header */}
                      <div
                        className={cn(
                          "w-full px-3 py-2 text-left text-sm flex items-center gap-2",
                          "cursor-pointer",
                          "transition-colors duration-100 hover:bg-secondary/50",
                          isCategorySelected && "bg-[#284899]/10"
                        )}
                        onClick={() => {
                          if (shouldShowTypes) {
                            // If category has expandable types, toggle expansion
                            handleCategoryToggle(group.category);
                          } else {
                            // If category has no expandable types, toggle selection directly
                            toggleCategorySelection(group.category);
                          }
                        }}
                      >
                        {shouldShowTypes && (
                          <ChevronRight
                            className={cn(
                              "h-3.5 w-3.5 text-muted-foreground transition-transform duration-200",
                              isExpanded && "rotate-90"
                            )}
                          />
                        )}
                        {!shouldShowTypes && (
                          <div className="h-3.5 w-3.5" /> // Spacer when no chevron
                        )}
                        {/* Category color dot */}
                        {(() => {
                          const categoryConfig = getCategoryConfig(group.category);
                          return (
                            <span
                              style={{
                                backgroundColor: categoryConfig.color,
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                flexShrink: 0,
                                boxShadow: "0 0 0 1px rgba(255,255,255,0.2)",
                              }}
                            />
                          );
                        })()}
                        <span
                          className={cn(
                            "font-medium flex-1",
                            isCategorySelected ? "text-[#284899]" : "text-foreground"
                          )}
                        >
                          {labelMap[group.category] || getLabel(group.category) || group.category}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {(() => {
                            // Use real count if available, otherwise fall back to types length
                            const categoryLabel = labelMap[group.category] || group.category;
                            const realCount = categoryCounts.categoryCounts[categoryLabel] ||
                              categoryCounts.categoryCounts[group.category];
                            return realCount !== undefined ? realCount : (shouldShowTypes ? group.types.length : 0);
                          })()}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCategorySelection(group.category);
                          }}
                          className={cn(
                            "h-6 w-6 flex items-center justify-center rounded-full border border-border transition-colors",
                            "bg-background/60 text-muted-foreground hover:border-[#284899] hover:text-foreground",
                            isCategorySelected && "border-[#284899] bg-[#284899]/10 text-[#284899]"
                          )}
                          aria-label="Seleziona categoria"
                        >
                          {isCategorySelected && <Check className="h-3 w-3" />}
                        </button>
                      </div>

                      {/* Expanded types - only show if shouldShowTypes is true */}
                      <AnimatePresence>
                        {shouldShowTypes && isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.15 }}
                            className="overflow-hidden"
                          >
                            {group.types.map((type) => {
                              const isTypeSelected = selectedTypes.includes(type);
                              const typeConfig = getTypologyConfig(type, group.category);
                              const TypeIcon = typeConfig.icon;

                              return (
                                <div
                                  key={type}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => toggleTypeSelection(group.category, type)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      toggleTypeSelection(group.category, type);
                                    }
                                  }}
                                  className={cn(
                                    "w-full pl-8 pr-3 py-1.5 text-left text-sm flex items-center gap-2",
                                    "transition-colors duration-100 hover:bg-secondary/50",
                                    isTypeSelected && "bg-[#284899]/10",
                                    "cursor-pointer"
                                  )}
                                >
                                  {/* Icon with color */}
                                  <TypeIcon
                                    className="h-3.5 w-3.5 shrink-0"
                                    style={{ color: typeConfig.color }}
                                  />
                                  {/* Color dot */}
                                  <span
                                    style={{
                                      backgroundColor: typeConfig.color,
                                      width: 6,
                                      height: 6,
                                      borderRadius: "50%",
                                      flexShrink: 0,
                                      boxShadow: "0 0 0 1px rgba(255,255,255,0.2)",
                                    }}
                                  />
                                  <span
                                    className={cn(
                                      "flex-1 truncate",
                                      isTypeSelected ? "text-[#284899] font-medium" : "text-foreground/80"
                                    )}
                                  >
                                    {getLabel(type)}
                                  </span>
                                  <span className="text-[10px] text-muted-foreground">
                                    {(() => {
                                      // Use real count if available
                                      const typeLabel = labelMap[type] || type;
                                      const realCount = categoryCounts.typeCounts[typeLabel] ||
                                        categoryCounts.typeCounts[type];
                                      return realCount !== undefined ? realCount : "";
                                    })()}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleTypeSelection(group.category, type);
                                    }}
                                    className={cn(
                                      "h-6 w-6 flex items-center justify-center rounded-full border border-border transition-colors",
                                      "bg-background/60 text-muted-foreground hover:border-[#284899] hover:text-foreground",
                                      isTypeSelected && "border-[#284899] bg-[#284899]/10 text-[#284899]"
                                    )}
                                    aria-label="Seleziona tipologia"
                                  >
                                    {isTypeSelected && <Check className="h-3 w-3" />}
                                  </button>
                                </div>
                              );
                            })}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Actions */}
            <div className="sticky bottom-0 left-0 right-0 bg-[hsl(var(--card))] border-t border-border px-3 py-2 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={clearSelection}
                className="text-[11px] text-muted-foreground hover:text-foreground transition-colors"
              >
                Azzera
              </button>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-muted-foreground">
                  {selectedCategories.length + selectedTypes.length} selezioni
                </span>
                <button
                  type="button"
                  onClick={closeDropdown}
                  className="text-[11px] text-foreground font-medium px-2 py-1 rounded-md hover:bg-secondary/50 transition-colors"
                >
                  Chiudi
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
